import { useCallback, useEffect, useRef } from "react";
import type { OwnerListPage, OwnerListQuery } from "@stackje/site-builder";
import { useOwnerPage } from "../../hooks/useOwnerPage";
import { shopRequest } from "./shopRequest";

/** Shop adapter: bound list requests and retain the last rows during a filter refresh. */
export function useShopPage<T extends { id: string }>(load: (site: string, query: OwnerListQuery, signal?: AbortSignal) => Promise<OwnerListPage<T>>, site: string, query: OwnerListQuery, revision: unknown) {
  const loadedSite = useRef<string | null>(null);
  const read = useCallback(async (id: string, q: OwnerListQuery, signal?: AbortSignal) => {
    const result = await shopRequest(inner => load(id, q, inner), signal);
    if (!signal?.aborted) loadedSite.current = id;
    return result;
  }, [load]);
  const page = useOwnerPage(read, site, query, revision);
  const retry = (page as typeof page & { retry?: () => void }).retry ?? page.more;
  const previous = useRef<{ site: string; rows: T[]; total: number }>({ site, rows: [], total: 0 });
  useEffect(() => {
    if (loadedSite.current === site && (!page.busy && !page.error || page.rows.length)) previous.current = { site, rows: page.rows, total: page.total };
  }, [site, page.busy, page.error, page.rows, page.total]);
  const retained = (page.busy || Boolean(page.error)) && !page.rows.length && previous.current.site === site;
  if (loadedSite.current !== site) return { ...page, retry, rows: [] as T[], total: 0 };
  return { ...page, retry, rows: retained ? previous.current.rows : page.rows, total: retained ? previous.current.total : page.total };
}
